// src/components/Chat.js
import React, { useState } from 'react';
import { Terminal } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import axios from 'axios';

const Chat = () => {
  const [messages, setMessages] = useState([
    { type: 'bot', content: 'Welcome to Sentionel Protocol. I am your blockchain security advisor. How can I assist you today?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const callOpenAI = async (userMessage) => {
    try {
      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: "gpt-4",
          messages: [
            {
              role: "system",
              content: "You are a blockchain security expert named Sentionel. Focus on providing analysis and insights about cryptocurrency tokens, blockchain projects, and security considerations. Provide detailed but concise responses about token analysis, smart contract security, and blockchain technology. If asked about specific tokens or projects, analyze them from a security perspective."
            },
            ...messages.map(msg => ({
              role: msg.type === 'user' ? 'user' : 'assistant',
              content: msg.content
            })),
            {
              role: "user",
              content: userMessage
            }
          ],
          temperature: 0.7,
        },
        {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data.choices[0].message.content;
    } catch (error) {
      console.error('Error calling OpenAI:', error);
      return "I apologize, but I'm having trouble connecting to my analysis systems. Please try again in a moment.";
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const botResponse = await callOpenAI(userMessage);
      setMessages(prev => [...prev, { type: 'bot', content: botResponse }]);
    } catch (error) {
      console.error('Error in chat:', error);
      setMessages(prev => [...prev, { 
        type: 'bot', 
        content: 'I encountered an error while analyzing your request. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="bg-gray-900 border-pink-500">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Terminal className="text-pink-500" />
          <CardTitle className="text-xl font-bold text-white">Security Advisor Chat</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-96 overflow-y-auto mb-4 space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx} className={`p-3 rounded-lg ${
              msg.type === 'user' ? 'bg-purple-900 ml-8' : 'bg-gray-800 mr-8'
            }`}>
              {msg.content}
            </div>
          ))}
          {isLoading && (
            <div className="bg-gray-800 mr-8 p-3 rounded-lg flex items-center gap-2">
              <div className="animate-pulse">Analyzing...</div>
            </div>
          )}
        </div>

        <form onSubmit={handleSend} className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 bg-gray-800 rounded p-2 border border-pink-500 focus:outline-none focus:border-purple-500"
            placeholder="Ask about blockchain security..."
            disabled={isLoading}
          />
          <button 
            type="submit"
            className={`bg-pink-600 hover:bg-pink-700 px-4 py-2 rounded ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isLoading}
          >
            Send
          </button>
        </form>
      </CardContent>
    </Card>
  );
};

export default Chat;